import { useCallback } from "react";
import { CommercialUnitDataWithBrand, CommercialUnitDataWithRate } from "../../types/commercial";
import { DriveTurnover } from "../../types/drive";
import { computeRHRate } from "../../utils/turnover";

type UseMetricsArgs = {
  currentTurnoverData: { averageBasket?: number; ticketsCount?: number; turnover?: number };
  pastTurnoverData: { averageBasket?: number; ticketsCount?: number; turnover?: number };
  currentTurnoverPromoData?: CommercialUnitDataWithRate;
  currentMemberCardTurnoverData?: CommercialUnitDataWithRate;
  pastTurnoverPromoData?: CommercialUnitDataWithRate;
  pastMemberCardTurnoverData?: CommercialUnitDataWithRate;
  currentDriveData: DriveTurnover;
  pastDriveData: DriveTurnover;
  currentTurnoverTriptychData?: CommercialUnitDataWithBrand[] | null;
  pastTurnoverTriptychData?: CommercialUnitDataWithBrand[] | null;
  strictRatioRangeValidation?: boolean;
};

export const useCommercialContentPageMetrics = ({
  currentTurnoverData,
  pastTurnoverData,
  currentTurnoverPromoData,
  currentMemberCardTurnoverData,
  pastTurnoverPromoData,
  pastMemberCardTurnoverData,
  currentDriveData,
  pastDriveData,
  currentTurnoverTriptychData,
  pastTurnoverTriptychData,
  strictRatioRangeValidation = false,
}: UseMetricsArgs) => {
  const basketRHRate = computeRHRate(
    currentTurnoverData?.averageBasket as number,
    pastTurnoverData?.averageBasket as number
  );
  const ticketsRHRate = computeRHRate(
    currentTurnoverData?.ticketsCount as number,
    pastTurnoverData?.ticketsCount as number
  );
  const promoRHRate = computeRHRate(
    currentTurnoverPromoData?.turnover as number,
    pastTurnoverPromoData?.turnover as number
  );
  const memberCardRHRate = computeRHRate(
    currentMemberCardTurnoverData?.turnover as number,
    pastMemberCardTurnoverData?.turnover as number
  );
  const turnoverSdvRHRate = computeRHRate(
    currentTurnoverData?.turnover as number,
    pastTurnoverData?.turnover as number
  );
  const driveRHRate = computeRHRate(
    currentDriveData?.turnover as number,
    pastDriveData?.turnover as number
  );

  const triptychTurnoverRates: () => (CommercialUnitDataWithBrand & { evol: number | null })[] =
    useCallback(
      () =>
        (currentTurnoverTriptychData ?? []).map((triptych) => {
          const pastTripTych = pastTurnoverTriptychData?.find((p) => p.brand === triptych.brand);

          return {
            ...triptych,
            evol: computeRHRate(
              triptych?.turnover as number,
              pastTripTych?.turnover as number
            ),
          } as CommercialUnitDataWithBrand & { evol: number | null };
        }),
      [currentTurnoverTriptychData, pastTurnoverTriptychData]
    );

  const formatNumber = (value: number | undefined) => {
    if (value === undefined || value === null) {
      return "";
    }
    const isMillion = Math.abs(value) >= 1000000;
    const isThousand = Math.abs(value) >= 1000 && !isMillion;
    const ratio = isMillion ? 1000000 : isThousand ? 1000 : 1;
    const currency = isMillion ? "M€" : isThousand ? "K€" : "€";

    return value
      ? `${new Intl.NumberFormat("fr-FR", {
          maximumFractionDigits: 1,
        }).format(Math.abs(value / ratio))} ${currency}`
      : "";
  };

  const ratioToPercentage = (ratio: number | undefined): number => {
    if (strictRatioRangeValidation) {
      if (!ratio || ratio > 1) {
        console.warn("Le ratio doit être compris entre 0 et 1.");
        return NaN;
      }
      return parseFloat((ratio * 100).toFixed(1));
    }

    if (ratio === undefined || ratio === null || Number.isNaN(ratio)) {
      return NaN;
    }

    return parseFloat((ratio * 100).toFixed(1));
  };

  return {
    basketRHRate,
    ticketsRHRate,
    promoRHRate,
    memberCardRHRate,
    turnoverSdvRHRate,
    driveRHRate,
    triptychTurnoverRates,
    formatNumber,
    ratioToPercentage,
  };
};
